import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TimesheetEntry } from '@/types/timesheet';
import { type BudgetContext, resolveInitialHtc } from '@/services/api/budgetContextApi';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export interface RowHoursToCompleteCellProps {
  entry: TimesheetEntry;
  index: number;
  isReadOnly: boolean;
  /** Budget context for this row's project. null = no data available. */
  budgetContext: BudgetContext | null;
  onUpdateEntry: (index: number, field: keyof TimesheetEntry, value: unknown) => void;
}

/**
 * "Hours remaining" cell — numeric input with a hover tooltip showing budget context.
 *
 * When a project is selected on the row (a new line, or a project change), the value is
 * defaulted once that project's budget context arrives (spec §2.4):
 *   1. budgetContext.recentSavedHtc !== null → carry forward the submitted value
 *   2. budgetContext.isAllocated && remainingHours > 0 → use remainingHours
 *   3. Otherwise (no allocation, or budget used up) → 0
 *
 * Saved rows loaded with a project already set are never defaulted. A defaulted value is
 * replaced when the project changes; a value the user typed or cleared is never touched.
 *
 * The field is always fully editable — no validation against remaining hours.
 */
export function RowHoursToCompleteCell(props: RowHoursToCompleteCellProps) {
  const { hoursToComplete } = useFeatureFlags();
  // Not rendered (and never auto-defaults a value) when the HoursToComplete feature is disabled.
  if (!hoursToComplete) return null;
  return <HoursToCompleteInput {...props} />;
}

function HoursToCompleteInput({
  entry,
  index,
  isReadOnly,
  budgetContext,
  onUpdateEntry,
}: RowHoursToCompleteCellProps) {
  const committed = entry.hoursToComplete != null ? entry.hoursToComplete.toFixed(2) : '';

  const [raw, setRaw] = useState<string>(committed);
  const [isEditing, setIsEditing] = useState(false);

  const onUpdateEntryRef = useRef(onUpdateEntry);
  onUpdateEntryRef.current = onUpdateEntry;
  const indexRef = useRef(index);
  indexRef.current = index;

  const projectId = entry.projectId || '';
  const previousProjectIdRef = useRef(projectId);
  const isUnsavedLine = entry.id < 0 && entry.sourceIntegration == null;
  /** Project selected on this row whose budget context should supply the default. */
  const pendingProjectIdRef = useRef<string | null>(
    isUnsavedLine && projectId !== '' && entry.hoursToComplete == null ? projectId : null,
  );
  const autoFilledValueRef = useRef<number | null>(null);
  const userOwnsValueRef = useRef(false);

  useEffect(() => {
    if (projectId === previousProjectIdRef.current) return;
    previousProjectIdRef.current = projectId;
    pendingProjectIdRef.current = null;
    if (userOwnsValueRef.current) return;

    const current = entry.hoursToComplete;
    const isAutoFilled = current != null && current === autoFilledValueRef.current;
    if (current != null && !isAutoFilled) return;

    if (isAutoFilled) {
      autoFilledValueRef.current = null;
      onUpdateEntryRef.current(indexRef.current, 'hoursToComplete', undefined);
    }
    if (projectId !== '') pendingProjectIdRef.current = projectId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (pendingProjectIdRef.current !== projectId || budgetContext === null) return;
    if (isReadOnly || isEditing) return;
    pendingProjectIdRef.current = null;
    if (userOwnsValueRef.current || entry.hoursToComplete != null) return;

    const defaultValue = resolveInitialHtc(budgetContext);
    if (defaultValue === null) return;
    autoFilledValueRef.current = defaultValue;
    onUpdateEntryRef.current(indexRef.current, 'hoursToComplete', defaultValue);
  }, [budgetContext, projectId, isReadOnly, isEditing, entry.hoursToComplete]);

  useEffect(() => {
    if (!isEditing) setRaw(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.hoursToComplete]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setRaw(e.target.value);
  }

  function handleBlur() {
    setIsEditing(false);
    const trimmed = raw.trim();
    if (trimmed === '') {
      if (entry.hoursToComplete != null) userOwnsValueRef.current = true;
      onUpdateEntry(index, 'hoursToComplete', undefined);
      return;
    }
    const parsed = parseFloat(trimmed);
    if (isNaN(parsed) || parsed < 0) {
      setRaw(committed);
      return;
    }
    const rounded = Math.round(parsed * 100) / 100;
    if (rounded !== entry.hoursToComplete) userOwnsValueRef.current = true;
    setRaw(String(rounded));
    onUpdateEntry(index, 'hoursToComplete', rounded);
  }

  /** Tooltip content showing budget context on hover. */
  const tooltipContent = budgetContext ? (
    <div className="text-xs space-y-1">
      <p className="font-semibold text-sm mb-1">Budget context</p>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">Budgeted</span>
        <span className="font-medium">{budgetContext.budgetHours} hrs</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">Logged to date</span>
        <span className="font-medium">{budgetContext.loggedHours} hrs</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">Remaining</span>
        <span className="font-medium">{budgetContext.remainingHours} hrs</span>
      </div>
    </div>
  ) : null;

  // Wraps children in a tooltip only when budget context is available.
  // Uses a <span> as the trigger (not asChild on Input) so Radix gets a
  // reliable ref-forwarding element and the tooltip fires correctly.
  const withTooltip = (child: React.ReactNode) => {
    if (!tooltipContent) return <>{child}</>;
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block w-full">{child}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="w-[200px]">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (isReadOnly) {
    if (entry.hoursToComplete == null) {
      return <td className="p-1 sm:p-1.5 text-center text-sm text-muted-foreground align-middle" />;
    }
    return (
      <td className="p-1 sm:p-1.5 text-center text-sm text-muted-foreground align-middle">
        {withTooltip(
          <span className="cursor-default">
            {entry.hoursToComplete.toFixed(2)}
          </span>
        )}
      </td>
    );
  }

  return (
    <td className="p-1 sm:p-1.5 align-middle">
      {withTooltip(
        <Input
          type="number"
          min="0"
          step="0.01"
          value={raw}
          onChange={handleChange}
          onFocus={() => setIsEditing(true)}
          onBlur={handleBlur}
          placeholder=""
          className="h-7 sm:h-8 text-center text-sm w-full px-1"
          aria-label="Hours remaining for this row"
        />
      )}
    </td>
  );
}
